<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ModalHeading from "@/components/global-themed/ui/modals/ModalHeading.vue";
import ModalSubText from "@/components/global-themed/ui/modals/ModalSubText.vue";
import ModalHeader from "@/components/global-themed/ui/modals/ModalHeader.vue";
import KitModalHeader from "@/components/global-themed/ui/modals/KitModalHeader.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import {onMounted, ref} from "vue";
import ModalTextAreaWrapper from "@/components/global-themed/ui/modals/ModalTextAreaWrapper.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  approvalId: {
    type: Number,
    required: true
  },
  category: {
    type: String,
    required: true
  }
});

const textareaRef = ref(null);
const feedback = ref('');
const errors = ref();
const showFormModal = ref(false);

const queryClient = useQueryClient();

onMounted(() => {
  if (textareaRef.value) {
    textareaRef.value.focus();
  }
})

const handleSubmit = () => {
  handleReject({
    feedback: feedback.value,
  })
}

const {mutate: handleReject, isPending} = useMutation({
  mutationFn: (data) => axios.patch(`/ajax/v2/approval-center/${props.approvalId}/reject`, data),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ['approvals', props.category]
    })

    emit('close');

    successToast('Successfully rejected this work');
  },
  onError: (error) => {
    let errorMessage = error.response?.data?.message || 'Something went wrong rejecting this work';

    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors;
    }

    errorToast(errorMessage);
  },
});
</script>

<template>
  <Teleport
    to=".tailwind"
  >
    <BaseModal
      v-if="!showFormModal"
      modal-class="tw-w-[720px]"
      @close="emit('close')"
    >
      <ModalContentWrapper>
        <KitModalHeader>
          <ModalHeader>
            <ModalHeading>Not quite right? Let’s fix it together.</ModalHeading>

            <ModalSubText>
              <p class="tw-mb-4">
                You’re about to reject this item of work. If you do, it won’t be replaced — but in most cases, we can improve it instead.
              </p>
              <p class="tw-mb-4">
                Want to remove services you don’t offer? Change the tone? Tweak the structure or focus on a different topic? You can do all that by leaving feedback — and we’ll make the changes for you. If you want to do this just click "Go Back" below and click on "Give Feedback"
              </p>
              <p>
                If you still want to reject this item and don’t want a replacement, click confirm below.
              </p>
            </ModalSubText>
          </ModalHeader>
        </KitModalHeader>
        <div class="tw-flex tw-items-center tw-gap-4 tw-self-end">
          <DefaultButton
            variant="primary-inverted"
            @click="emit('close')"
          >
            Go Back
          </DefaultButton>
          <DefaultButton
            @click.prevent="showFormModal = true"
          >
            Yes, Please Reject
          </DefaultButton>
        </div>
      </ModalContentWrapper>
    </BaseModal>

    <BaseModal
      v-if="showFormModal"
      modal-class="tw-w-[720px]"
      @close="emit('close')"
    >
      <ModalContentWrapper>
        <KitModalHeader>
          <ModalHeader>
            <ModalHeading>Are you sure you want to reject this work?</ModalHeading>
          </ModalHeader>
        </KitModalHeader>
        <div class="tw-flex tw-flex-col tw-gap-6">
          <span class="tw-flex tw-gap-3 tw-text-neutral-900 tw-font-bold tw-text-2xl tw-leading-none">
            <FixPanelIcon
              icon-path="icons/users-talking"
              class="tw-h-6 tw-w-6 tw-text-neutral tw-shrink-0 tw-grow-0"
            />
            Please give me some feedback on why you are rejecting, it will help me get better:
          </span>

          <ModalTextAreaWrapper :error="!!errors?.feedback">
            <div>
              <textarea
                ref="textareaRef"
                v-model="feedback"
                autofocus
                rows="8"
                placeholder="Type your feedback here..."
                class="tw-w-full tw-text-neutral-900 tw-text-base tw-resize-none"
              />
              <p
                v-if="errors?.feedback"
                class="tw-text-danger"
              >
                {{ errors.feedback[0] }}
              </p>
            </div>
          </ModalTextAreaWrapper>
        </div>
        <div class="tw-flex tw-items-center tw-gap-4 tw-self-end">
          <DefaultButton
            variant="primary-inverted"
            @click="emit('close')"
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            :disabled="isPending"
            :loading="isPending"
            @click="handleSubmit"
          >
            Yes, Please Reject
          </DefaultButton>
        </div>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>
</template>
